/**
 * useAuth Hook
 * Provides authentication functionality with MSAL React and Jotai state management.
 * Uses the official @azure/msal-react hooks which work with MsalProvider.
 */

import { useCallback, useEffect, useRef } from "react";
import { useAtom, useSetAtom } from "jotai";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import {
  userStateAtom,
  msalAccountAtom,
  authLoadingAtom,
  authErrorAtom,
  pagesAccessibleAtom,
  userPhotoAtom,
} from "@/store/userStore";
import { acquireApiToken, apiScopes } from "@/config/msalConfig";
import { setAuthTokenGetter, setApiAuthToken } from "@/services";
import type { IUserState, IMenuItem } from "@/services";
import { ENABLE_MOCK_AUTH } from "@/lib/env";
import { logger } from "@/utils/logger";
import * as Sentry from "@sentry/react";

// API base URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";
const MOCK_AUTH_ENABLED = ENABLE_MOCK_AUTH;

const mockUser: IUserState = {
  id: 0,
  email: "mock.user@example.com",
  name: "Mock User",
  role_level: 2,
  organization_settings: {
    hours_in_day: 8,
    disable_fravaer: false,
    include_holidays: false,
    include_weekends: false,
  },
};

const mockPages: IMenuItem[] = [
  { id: 1, name: "Booking", route: "/booking" },
  { id: 2, name: "Admin", route: "/admin" },
];

export interface UseAuthReturn {
  /** Initialize MSAL and check for existing session */
  initialize: () => Promise<void>;
  /** Trigger login redirect */
  login: () => Promise<void>;
  /** Trigger logout redirect */
  logout: () => void;
  /** Get current access token */
  getToken: () => Promise<string | null>;
  /** Authentication loading state */
  isLoading: boolean;
  /** Authentication error */
  error: Error | null;
  /** Clear error state */
  clearError: () => void;
}

export function useAuth(): UseAuthReturn {
  // MSAL React hooks - these work with MsalProvider context
  const { instance, accounts, inProgress } = useMsal();

  // Jotai state for app-wide user data
  const [, setUser] = useAtom(userStateAtom);
  const setMsalAccount = useSetAtom(msalAccountAtom);
  const [isLoading, setIsLoading] = useAtom(authLoadingAtom);
  const [error, setError] = useAtom(authErrorAtom);
  const setPagesAccessible = useSetAtom(pagesAccessibleAtom);
  const setUserPhoto = useSetAtom(userPhotoAtom);

  // Track if we've already initialized to prevent double-fetch
  const initializedRef = useRef(false);

  // Set up auth token getter for services
  // Both ServicesAPI and apiClient need the token getter configured
  useEffect(() => {
    setAuthTokenGetter(acquireApiToken);
    setApiAuthToken(acquireApiToken);
  }, []);

  // Fetch user data from API
  // Returns { user, accessDenied } to distinguish 403 from other errors
  const fetchUserData = useCallback(
    async (
      token: string,
    ): Promise<{ user: IUserState | null; accessDenied: boolean }> => {
      try {
        const response = await fetch(`${API_BASE_URL}/user`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          // Distinguish 403 (access denied) from other errors
          if (response.status === 403) {
            logger.warn("Access denied: User not authorized (403)");
            return { user: null, accessDenied: true };
          }
          throw new Error(`Failed to fetch user data: ${response.status}`);
        }

        const user = await response.json();
        return { user, accessDenied: false };
      } catch (err) {
        logger.error("Error fetching user data:", err);
        return { user: null, accessDenied: false };
      }
    },
    [],
  );

  // Fetch accessible pages from API
  const fetchAccessiblePages = useCallback(
    async (token: string): Promise<IMenuItem[]> => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/organization/pages/accessible`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          logger.warn(`Failed to fetch accessible pages: ${response.status}`);
          return [];
        }

        return response.json();
      } catch (err) {
        logger.error("Error fetching accessible pages:", err);
        return [];
      }
    },
    [],
  );

  // Fetch user photo from API
  const fetchUserPhoto = useCallback(async (token: string): Promise<string> => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/photo`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 404) {
        // No photo available
        return "";
      }

      if (!response.ok) {
        logger.warn(`Failed to fetch user photo: ${response.status}`);
        return "";
      }

      const body = (await response.json()) as { imagedata: string };
      return `data:image/jpeg;base64,${body.imagedata}`;
    } catch (err) {
      logger.error("Error fetching user photo:", err);
      return "";
    }
  }, []);

  // Initialize - fetch user data after MSAL authentication is complete
  // MsalProvider handles redirect and authentication automatically
  const initialize = useCallback(async () => {
    if (MOCK_AUTH_ENABLED) {
      if (initializedRef.current) {
        return;
      }

      initializedRef.current = true;
      setUser(mockUser);
      setPagesAccessible(mockPages);
      setUserPhoto("");
      setMsalAccount(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Don't do anything while MSAL is handling interaction
    if (inProgress !== InteractionStatus.None) {
      logger.debug("MSAL interaction in progress:", inProgress);
      setIsLoading(true);
      return;
    }

    // Prevent double initialization
    if (initializedRef.current) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get account from MSAL React context
      const account = accounts[0] || instance.getActiveAccount() || null;

      // If no account and no interaction in progress, trigger login
      if (!account) {
        logger.debug("No account found, redirecting to login...");
        await instance.loginRedirect({
          scopes: ["openid", "profile", ...apiScopes.scopes],
        });
        // This will redirect away, so we return here
        return;
      }

      // Mark as initialized
      initializedRef.current = true;

      // We have an account, set it and fetch data
      logger.debug("Account found:", account.username);
      setMsalAccount(account);

      // Acquire token and fetch user data
      const token = await acquireApiToken(account);
      if (token) {
        // Fetch user data, accessible pages, and photo in parallel
        const [userResult, pages, photo] = await Promise.all([
          fetchUserData(token),
          fetchAccessiblePages(token),
          fetchUserPhoto(token),
        ]);

        if (userResult.accessDenied) {
          // User authenticated with Azure AD but not authorized in our system
          setError(new Error("ACCESS_DENIED"));
          setUser(null);
          setMsalAccount(null); // Clear MSAL account so isAuthenticated becomes false
          Sentry.setUser(null);
        } else if (userResult.user) {
          setUser(userResult.user);
          // Set Sentry user context (ID only, no PII)
          Sentry.setUser({ id: String(userResult.user.id) });
        }
        setPagesAccessible(pages);
        setUserPhoto(photo);
      }
    } catch (err) {
      logger.error("Auth initialization error:", err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [
    instance,
    accounts,
    inProgress,
    setUser,
    setMsalAccount,
    setIsLoading,
    setError,
    setPagesAccessible,
    setUserPhoto,
    fetchUserData,
    fetchAccessiblePages,
    fetchUserPhoto,
  ]);

  // Login
  const login = useCallback(async () => {
    setError(null);

    if (MOCK_AUTH_ENABLED) {
      return;
    }

    try {
      await instance.loginRedirect({
        scopes: apiScopes.scopes,
      });
    } catch (err) {
      logger.error("Login error:", err);
      setError(err as Error);
    }
  }, [instance, setError]);

  // Logout
  const logout = useCallback(() => {
    initializedRef.current = false;
    setUser(null);
    setMsalAccount(null);
    Sentry.setUser(null);
    if (!MOCK_AUTH_ENABLED) {
      instance.logoutRedirect();
    }
  }, [instance, setUser, setMsalAccount]);

  // Get token
  const getToken = useCallback(async () => {
    return acquireApiToken();
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  return {
    initialize,
    login,
    logout,
    getToken,
    isLoading,
    error,
    clearError,
  };
}
